// Settings panel (§1.9), onboarding tour, toasts, and the dialog host.

import { lazy, Suspense, useEffect, useRef } from 'react';
import { useStore, setView, setState, closeModal, getState, type ViewPrefs } from '../state/store';
import { saveJson, loadJson } from '../state/persist';
import { Segmented, Toggle } from './common';
import { setZoom, ZOOM_MIN, ZOOM_MAX } from './zoom';
import { track } from '../analytics';
// Dialogs are code-split: each group loads on first use.
const theory = () => import('./dialogs/TheoryDialogs');
const misc = () => import('./dialogs/MiscDialogs');
const inst = () => import('./dialogs/InstrumentDialogs');
const app = () => import('./dialogs/AppDialogs');
const TransposeDialog = lazy(() => theory().then((m) => ({ default: m.TransposeDialog })));
const KeySigDialog = lazy(() => theory().then((m) => ({ default: m.KeySigDialog })));
const TimeSigDialog = lazy(() => theory().then((m) => ({ default: m.TimeSigDialog })));
const ClefDialog = lazy(() => theory().then((m) => ({ default: m.ClefDialog })));
const TempoDialog = lazy(() => theory().then((m) => ({ default: m.TempoDialog })));
const M = {
  TupletDialog: lazy(() => misc().then((m) => ({ default: m.TupletDialog }))),
  EndingsDialog: lazy(() => misc().then((m) => ({ default: m.EndingsDialog }))),
  DirectionDialog: lazy(() => misc().then((m) => ({ default: m.DirectionDialog }))),
  ChordDialog: lazy(() => misc().then((m) => ({ default: m.ChordDialog }))),
  TextDialog: lazy(() => misc().then((m) => ({ default: m.TextDialog }))),
  LyricsDialog: lazy(() => misc().then((m) => ({ default: m.LyricsDialog }))),
  SectionDialog: lazy(() => misc().then((m) => ({ default: m.SectionDialog }))),
  MultiRestDialog: lazy(() => misc().then((m) => ({ default: m.MultiRestDialog }))),
  BarNumberDialog: lazy(() => misc().then((m) => ({ default: m.BarNumberDialog }))),
  FingeringDialog: lazy(() => misc().then((m) => ({ default: m.FingeringDialog }))),
  ColorDialog: lazy(() => misc().then((m) => ({ default: m.ColorDialog }))),
  CommentaryDialog: lazy(() => misc().then((m) => ({ default: m.CommentaryDialog }))),
  SimpleBeatStringDialog: lazy(() => misc().then((m) => ({ default: m.SimpleBeatStringDialog }))),
};
const InstrumentsDialog = lazy(() => inst().then((m) => ({ default: m.InstrumentsDialog })));
const AddInstrumentDialog = lazy(() => inst().then((m) => ({ default: m.AddInstrumentDialog })));
const InstrumentFormDialog = lazy(() => inst().then((m) => ({ default: m.InstrumentFormDialog })));
const DetailsDialog = lazy(() => app().then((m) => ({ default: m.DetailsDialog })));
const VersionsDialog = lazy(() => app().then((m) => ({ default: m.VersionsDialog })));
const PrefsDialog = lazy(() => app().then((m) => ({ default: m.PrefsDialog })));
const ShortcutsDialog = lazy(() => app().then((m) => ({ default: m.ShortcutsDialog })));
const LoginDialog = lazy(() => app().then((m) => ({ default: m.LoginDialog })));
const BugReportDialog = lazy(() => app().then((m) => ({ default: m.BugReportDialog })));
import { openModal } from '../state/store';
import * as player from '../playback/player';

export function DialogHost() {
  const modal = useStore((s) => s.modal);
  if (!modal) return null;
  return (
    <Suspense fallback={<div className="modal-backdrop" aria-busy="true" />}>
      <DialogSwitch />
    </Suspense>
  );
}

function DialogSwitch() {
  const modal = useStore((s) => s.modal);
  if (!modal) return null;
  const p = modal.props ?? {};
  switch (modal.kind) {
    case 'transpose':
      return <TransposeDialog />;
    case 'keysig':
      return <KeySigDialog />;
    case 'timesig':
      return <TimeSigDialog />;
    case 'clef':
      return <ClefDialog />;
    case 'tempo':
      return <TempoDialog />;
    case 'tuplet':
      return <M.TupletDialog />;
    case 'endings':
      return <M.EndingsDialog />;
    case 'direction':
      return <M.DirectionDialog />;
    case 'chord':
      return <M.ChordDialog />;
    case 'text':
      return <M.TextDialog k={(p.key as 'above') ?? 'above'} />;
    case 'lyrics':
      return <M.LyricsDialog />;
    case 'section':
      return <M.SectionDialog field={(p.field as 'name') ?? 'name'} />;
    case 'multirest':
      return <M.MultiRestDialog />;
    case 'barnumber':
      return <M.BarNumberDialog />;
    case 'fingering':
      return <M.FingeringDialog k={(p.key as 'fingering') ?? 'fingering'} />;
    case 'color':
      return <M.ColorDialog />;
    case 'commentary':
      return <M.CommentaryDialog />;
    case 'sticking':
      return <M.SimpleBeatStringDialog k="sticking" />;
    case 'roman':
      return <M.SimpleBeatStringDialog k="roman" />;
    case 'instruments':
      return <InstrumentsDialog />;
    case 'addInstrument':
      return <AddInstrumentDialog />;
    case 'instrument':
      return <InstrumentFormDialog index={Number(p.index ?? 0)} />;
    case 'details':
      return <DetailsDialog />;
    case 'versions':
      return <VersionsDialog />;
    case 'prefs':
      return <PrefsDialog />;
    case 'shortcuts':
      return <ShortcutsDialog />;
    case 'login':
      return <LoginDialog mode={(p.mode as 'login') ?? 'login'} error={typeof p.error === 'string' ? p.error : undefined} />;
    case 'bugReport':
      return <BugReportDialog />;
    default:
      return null;
  }
}

export function SettingsPanel() {
  const open = useStore((s) => s.settingsOpen);
  const view = useStore((s) => s.view);
  const mode = useStore((s) => s.mode);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !(e.target as HTMLElement).closest('[aria-label="Settings"]')) setState({ settingsOpen: false });
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);
  if (!open) return null;
  const set = (p: Partial<ViewPrefs>) => setView(p);
  return (
    <aside className="settings-panel" ref={ref} role="dialog" aria-label="Settings">
      <header>
        <h2>Settings</h2>
        <button className="modal-close" aria-label="Close settings" onClick={() => setState({ settingsOpen: false })}>
          ×
        </button>
      </header>
      <section>
        <h3>Instruments</h3>
        <button className="btn small" onClick={() => openModal('instruments')}>
          Manage instruments, tuning & mix…
        </button>
      </section>
      <section>
        <h3>Layout</h3>
        <Segmented label="Layout" value={view.layout} onChange={(v) => set({ layout: v })} options={[{ value: 'scroll', label: 'Scroll' }, { value: 'paged', label: 'Paged' }, { value: 'horizontal', label: 'Horizontal' }]} />
        <label className="pop-row">
          <span>Zoom</span>
          <input type="range" min={ZOOM_MIN} max={ZOOM_MAX} step={0.05} value={view.zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          <span>{Math.round(view.zoom * 100)}%</span>
        </label>
        <Toggle label="Show title" checked={view.showTitle} onChange={(v) => set({ showTitle: v })} />
      </section>
      <section>
        <h3>Appearance</h3>
        <label className="pop-row">
          <span>Note colors</span>
          <select value={view.noteColors} onChange={(e) => set({ noteColors: e.target.value as ViewPrefs['noteColors'] })}>
            <option value="hide">Hide</option>
            <option value="authored">As authored</option>
            <option value="boomwhackers">Boomwhackers</option>
          </select>
        </label>
        <label className="pop-row">
          <span>Note names</span>
          <select value={view.noteNames} onChange={(e) => set({ noteNames: e.target.value as ViewPrefs['noteNames'] })}>
            <option value="none">Disabled</option>
            <option value="abc">A-B-C</option>
            <option value="german">German (H)</option>
            <option value="degree">Scale degree</option>
            <option value="solfege-fixed">Solfège (fixed do)</option>
            <option value="solfege-movable">Solfège (movable do)</option>
          </select>
        </label>
        <Toggle label="Left-handed fretboard" checked={view.leftHanded} onChange={(v) => set({ leftHanded: v })} />
      </section>
      <section>
        <h3>Volume</h3>
        <input type="range" min={0} max={1} step={0.05} value={view.volume} onChange={(e) => set({ volume: Number(e.target.value) })} aria-label="Master volume" />
      </section>
      <section>
        <h3>Transposition (view only)</h3>
        {mode === 'edit' ? (
          <p className="muted small">Use view mode to change the notation on the fly. In edit mode, use Notes → Transpose.</p>
        ) : (
          <label className="pop-row">
            <select value={view.viewTranspose} onChange={(e) => set({ viewTranspose: Number(e.target.value) })} aria-label="View transposition">
              {Array.from({ length: 25 }, (_, i) => i - 12).map((n) => (
                <option key={n} value={n}>
                  {n > 0 ? `+${n}` : n} semitone{Math.abs(n) === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>
      <section>
        <h3>Playhead</h3>
        <Toggle label="Follow playhead" checked={view.followPlayhead} onChange={(v) => set({ followPlayhead: v })} />
      </section>
      <section>
        <h3>Audio latency</h3>
        <label className="pop-row">
          <input type="range" min={-200} max={400} step={10} value={view.latencyMs} onChange={(e) => set({ latencyMs: Number(e.target.value) })} aria-label="Audio latency" />
          <span>{view.latencyMs} ms</span>
        </label>
      </section>
      <section className="row">
        <button
          className="btn small"
          onClick={() => {
            if (document.fullscreenElement) void document.exitFullscreen();
            else void document.documentElement.requestFullscreen?.();
          }}
        >
          Fullscreen
        </button>
        <button className="btn small" onClick={() => openModal('shortcuts')}>
          Keyboard shortcuts
        </button>
      </section>
      <p className="muted small">Settings save automatically.</p>
    </aside>
  );
}

interface TourStep {
  title: string;
  body: string;
  target: string;
}

const TOUR_STAFF: TourStep[] = [
  { title: 'Click a beat to put your cursor there', body: 'Click anywhere on the staff or tab. A coral circle marks the cursor; the selected beat turns coral.', target: '.score-scroll' },
  { title: 'Type letters to enter notes', body: 'On the staff, type A–G. Each note lands in the octave nearest the previous one. Type 2–8 to add an interval above (hold Shift for below).', target: '.score-scroll' },
  { title: 'Use the arrow keys to move', body: '→ moves to the next beat. At the end of a bar it adds a new beat or a new bar. + and − change the duration.', target: '.score-scroll' },
  { title: 'Or type fret numbers on the tab', body: 'Click a tab line, then type the fret. Two digits typed quickly make one fret (1, 2 → 12). ↑/↓ change string.', target: '.score-scroll' },
  { title: 'Common notations are on the left', body: 'Durations, accidentals, ties, dots, rests and grace notes. Commands you use from the ribbon show up under “Recent”.', target: '[data-tour="palette"]' },
  { title: 'More notations are at the top', body: 'The ribbon groups everything else: bars, repeats, guitar techniques, dynamics, text and formatting.', target: '[data-tour="ribbon"]' },
  { title: 'Search every command', body: 'Press ⌘K (Ctrl+K) and type what you want — “transpose”, “key sig”, “bend”.', target: '[data-tour="search"]' },
];

export function Tour() {
  const step = useStore((s) => s.tourStep);
  useEffect(() => {
    if (step === null && !loadJson<boolean>('fl.tourDone', false) && location.pathname !== '/') {
      const t = setTimeout(() => setState({ tourStep: 0 }), 600);
      return () => clearTimeout(t);
    }
  }, [step]);
  if (step === null) return null;
  const s = TOUR_STEPS[step];
  if (!s) return null;
  const done = () => {
    track(step === TOUR_STEPS.length - 1 ? 'tour_complete' : 'tour_skip', { step: step + 1 });
    saveJson('fl.tourDone', true);
    setState({ tourStep: null });
  };
  const el = document.querySelector(s.target);
  const rect = el?.getBoundingClientRect();
  // Steps that point at the score sit at the bottom of the score area so the
  // first system stays visible; the others sit next to their target.
  const onScore = s.target === '.score-scroll';
  const style: React.CSSProperties = rect
    ? {
        left: Math.min(window.innerWidth - 340, Math.max(12, rect.left + 24)),
        top: onScore ? Math.max(rect.top + 24, Math.min(window.innerHeight - 220, rect.bottom - 220)) : Math.min(window.innerHeight - 200, Math.max(12, rect.top + 24)),
      }
    : { left: 24, top: 120 };
  return (
    <div className="tour-card" style={style} role="dialog" aria-label="Tour">
      <div className="tour-step">
        Step {step + 1} of {TOUR_STEPS.length}
      </div>
      <h3>{s.title}</h3>
      <p>{s.body}</p>
      <div className="row">
        <button className="btn small" disabled={step === 0} onClick={() => setState({ tourStep: step - 1 })}>
          Previous
        </button>
        {step < TOUR_STEPS.length - 1 ? (
          <button className="btn small primary" onClick={() => setState({ tourStep: step + 1 })}>
            Next
          </button>
        ) : (
          <button className="btn small primary" onClick={done}>
            Done
          </button>
        )}
        <button className="btn small link" onClick={done}>
          Done for now
        </button>
      </div>
    </div>
  );
}
const TOUR_STEPS = TOUR_STAFF;

export function Toasts() {
  const toasts = useStore((s) => s.toasts);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.kind}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

export { closeModal, getState, player };
